import { useAppSelector } from "app/hooks";
import { useEffect, useMemo, useRef } from "react";
import { useObjectOnlyDiff } from "../helpers";

type Output = Record<string, boolean>;

type UseShowHideLayers = (krpanoFeaturesReady: boolean) => void;

/**
 * # Show/Hide Layers control for krpano
 *
 * @param krpanoFeaturesReady see `AppInner`
 */
export const useShowHideLayers: UseShowHideLayers = (krpanoFeaturesReady) => {
  const fieldLayers = useAppSelector((state) => state.krpano.layers.fields);
  const featureLayers = useAppSelector((state) => state.krpano.layers.features);
  const groups: Output = useMemo(
    () => ({
      ...featureLayers.reduce<Output>((acc, { groupName, visible }) => {
        acc[groupName] = visible;
        return acc;
      }, {}),
      ...fieldLayers.reduce<Output>((acc, { name, visible }) => {
        acc[name] = visible;
        return acc;
      }, {}),
    }),
    [fieldLayers, featureLayers]
  );

  useShowHideDifference(krpanoFeaturesReady, groups);
};

type UseShowHideDifference = (
  krpanoFeaturesReady: boolean,
  groups: Output
) => void;

/**
 * Calculate and respond to the difference between previous and current layers
 */
const useShowHideDifference: UseShowHideDifference = (
  krpanoFeaturesReady,
  groups
) => {
  const prevLayers = useRef<typeof groups>(groups);

  // Difference between previous and current groups
  const diff = useObjectOnlyDiff<typeof groups>(
    prevLayers.current ?? {},
    groups
  );

  // Set visibility on initial load (after `krpanoFeaturesReady`)
  useEffect(() => {
    if (krpanoFeaturesReady) {
      // Apply initial load values to krpano
      updateGroupVisibility(groups);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [krpanoFeaturesReady]);

  // Set visibility on change
  useEffect(() => {
    if (krpanoFeaturesReady) {
      // Set "prev" with "current"
      prevLayers.current = groups;
      // Apply changes to krpano
      updateGroupVisibility(diff);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [krpanoFeaturesReady, diff]);
};

const updateGroupVisibility = (groups: Partial<Output>) => {
  Object.entries(groups).forEach(([groupName, groupValue]) => {
    window.krpano.call(`visibilityByGroup(${groupName}, ${groupValue});`);
  });
};
